import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { roleLabel } from './memberLabels';

const Section = ({ title, items, render }) => {
  if (!items || items.length === 0) return null;
  return (
    <Box mt={2}>
      <Typography variant="subtitle2">{title}</Typography>
      <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} mt={0.5}>
        {items.map(render)}
      </Box>
    </Box>
  );
};

/**
 * The confirm step for removing a school member. Removal silently changes another
 * user's access and the school's licence accounting, so this renders the backend's
 * removal preview in full, refuses to confirm while there are blockers, and makes the
 * operator type the member's email before Confirm turns on.
 */
const RemoveMemberDialog = ({ open, preview, schoolName, onClose, onConfirm }) => {
  const [typedEmail, setTypedEmail] = useState('');

  useEffect(() => {
    if (open) setTypedEmail('');
  }, [open]);

  if (!preview) return null;

  const { user, roles, seats, classrooms, students, blockers } = preview;
  const blocked = blockers && blockers.length > 0;
  const emailMatches =
    typedEmail.trim().toLowerCase() === (user.email || '').toLowerCase();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Remove {user.name} from the school?</DialogTitle>
      <DialogContent>
        <Alert severity="error">
          <AlertTitle>This is destructive and cannot be undone</AlertTitle>
          {user.name} ({user.email}) will lose <b>everything</b> they hold at{' '}
          {schoolName || 'this school'}, across every product: every role, seat and
          pending invite, their place on class rosters, and their curriculum and report
          access. Adding them again is the only way back, and they will need new seats.
        </Alert>

        {blocked && (
          <Box mt={2}>
            <Alert severity="warning">
              <AlertTitle>This member cannot be removed yet</AlertTitle>
              {blockers.map(({ code, message }) => (
                <Typography key={code} variant="body2">
                  • {message}
                </Typography>
              ))}
            </Alert>
          </Box>
        )}

        <Section
          title="Roles removed"
          items={roles}
          render={(role) => <Chip key={role} size="small" label={roleLabel(role)} />}
        />
        <Section
          title="Seats revoked"
          items={seats}
          render={(seat) => (
            <Chip
              key={seat.licenseId}
              size="small"
              label={`${roleLabel(seat.licenseType)} · ${seat.subscriptionId}`}
            />
          )}
        />
        <Section
          title="Removed from classrooms"
          items={classrooms}
          render={(c) => <Chip key={c._id} size="small" label={c.sectionName} />}
        />
        <Section
          title="Children leaving the rosters"
          items={students}
          render={(s) => <Chip key={s._id} size="small" label={s.name} />}
        />

        <Box mt={2}>
          <Typography variant="body2">
            Licences revoked: <b>{preview.licenses}</b> · Pending invites revoked:{' '}
            <b>{preview.pendingInvites}</b> · Seats returned to the subscription:{' '}
            <b>{preview.quotaToReturn}</b>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Students, submissions, observations and attendance are kept.
          </Typography>
        </Box>

        {!blocked && (
          <Box mt={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={`Type ${user.email} to confirm`}
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="secondary"
          disabled={blocked || !emailMatches}>
          Remove Member
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveMemberDialog;
